import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, SEASON_OPTIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export default function Onboarding() {
    const { user, refreshUser } = useAuth();
    const [selected, setSelected] = useState(user?.seasons || []);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const toggle = (s) => {
        setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const submit = async () => {
        if (selected.length === 0) return;
        setBusy(true);
        try {
            await api.post("/season", { seasons: selected });
            await refreshUser();
            navigate("/assessment");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground grain">
            <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 relative z-[2]">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="eyebrow-gold mb-6">Before we begin</div>
                    <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light">
                        What has God entrusted to you most in this season?
                    </h1>
                    <div className="mt-8 h-px w-16 bg-[hsl(var(--eden-gold))]" />
                    <p className="mt-8 text-muted-foreground text-lg max-w-2xl leading-relaxed">
                        Choose one or more. There is no rush and no wrong answer — your quiet honesty here will
                        gently shape the rest of your garden.
                    </p>
                </motion.div>

                <div className="mt-14 flex flex-wrap gap-2.5" data-testid="season-options">
                    {SEASON_OPTIONS.map((s, i) => {
                        const active = selected.includes(s);
                        return (
                            <motion.button
                                key={s}
                                onClick={() => toggle(s)}
                                data-testid={`season-option-${i}`}
                                aria-pressed={active}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
                                className={`chip ${active ? "is-active" : ""}`}
                            >
                                {active && <Check className="w-3 h-3" strokeWidth={2} />}
                                <span>{s}</span>
                            </motion.button>
                        );
                    })}
                </div>

                <div className="mt-14 flex items-center gap-6">
                    <Button
                        onClick={submit}
                        disabled={selected.length === 0 || busy}
                        data-testid="onboarding-continue-btn"
                        className="h-12 px-8 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md text-[11px] uppercase tracking-[0.32em]"
                    >
                        {busy ? "Preparing…" : "Continue to the Blueprint"}
                    </Button>
                    <span className="eyebrow" data-testid="season-selected-count">
                        {selected.length} {selected.length === 1 ? "chosen" : "chosen"}
                    </span>
                </div>
            </div>
        </div>
    );
}
